import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { assertPagePermission } from "@/features/auth/lib/assert-page-permission";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { PageError, PageAccessErrorComponent } from "@/features/auth/components/PageError";
import { BrandBrainPage } from "@/features/brand-brain/components/BrandBrainPage";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { usePageState } from "@/hooks/usePageState";
import { brandBrainQueryOptions, pageStateQueryOptions, PageAccessError } from "@/lib/queries";

export const Route = createFileRoute("/_authenticated/brand-brain")({
  beforeLoad: () => assertPagePermission("brand-brain"),
  loader: async ({ context }) => {
    const pageState = await context.queryClient
      .ensureQueryData(pageStateQueryOptions("brand-brain"))
      .catch((err: unknown) => {
        if (err instanceof PageAccessError) return null;
        throw err;
      });

    const tenantId = useAuth.getState().activeTenantId;
    if (tenantId) {
      void context.queryClient.prefetchQuery(brandBrainQueryOptions(tenantId));
    }

    return pageState;
  },
  component: BrandBrainRoute,
  errorComponent: PageAccessErrorComponent,
});

function BrandBrainRoute() {
  const { t } = useTranslation();
  useDocumentTitle(t("page.title.brandBrain", "Brand Brain"));
  const { isLoading, error } = usePageState("brand-brain");

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground" role="status">
        {t("brandBrain.loading", "Loading Brand Brain…")}
      </div>
    );
  }

  if (error) {
    return <PageError is403={error instanceof PageAccessError} />;
  }

  return <BrandBrainPage />;
}
